import {
  Button,
  CustomDatePicker,
  FieldInfo,
  Input,
  Modal,
} from '@/components';
import { Spinner } from '@/components/Spinner';
import { UserRateSchema, UserRateSchemaType } from '@/modules/Users/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { ReactElement, useEffect, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface HourlyRateModalProps {
  handleOnSubmit: (e: UserRateSchemaType) => void;
  handleClose: () => void;
  isLoading: boolean;
  disableFields?: (keyof UserRateSchemaType)[];
  type: 'create' | 'edit';
  isOpen: boolean;
  title: string;
  value?: number;
  jiraAccountId?: string;
  startDate?: Date;
  endDate?: Date;
  minDate?: Date;
}

export const HourlyRateModal = ({
  handleOnSubmit,
  handleClose,
  isLoading,
  type,
  isOpen,
  title,
  value,
  jiraAccountId,
  startDate,
  endDate,
  minDate,
}: HourlyRateModalProps): ReactElement => {
  const { t } = useTranslation();

  const {
    clearErrors,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    watch,
  } = useForm<UserRateSchemaType>({
    resolver: zodResolver(UserRateSchema),
    mode: 'onBlur',
    criteriaMode: 'all',
    progressive: true,
  });

  useEffect(() => {
    clearErrors();

    setValue('rate', value?.toString() || '');
    setValue('startDate', minDate || startDate || new Date());
    setValue('endDate', endDate);
    setValue('jiraAccountId', jiraAccountId || '');
  }, [
    clearErrors,
    setValue,
    value,
    jiraAccountId,
    startDate,
    endDate,
    isOpen,
    minDate,
  ]);

  const onSubmit: SubmitHandler<UserRateSchemaType> = (data) => {
    handleOnSubmit(data);
  };

  const minEndDate = watch('startDate');
  const maxStartDate = useMemo(() => {
    if (type === 'edit' && endDate) {
      return watch('endDate');
    }
  }, [endDate, type, watch]);

  return (
    <Modal
      isOpen={isOpen}
      title={title}
      className="h-fit w-128"
      handleClose={handleClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="h-70 mt-2 flex flex-col overflow-y-auto p-2">
          <FieldInfo
            title={t('hourly_rate_value')}
            errorText={
              errors.rate?.message ? t(errors.rate.message) : undefined
            }
            hasError={!!errors.rate?.message}
          >
            <Input
              type="number"
              step="0.01"
              isDisabled={isLoading}
              className="w-full bg-brightGray italic"
              placeHolder={'0 €'}
              name="rate"
              register={register}
            />
          </FieldInfo>
          <FieldInfo
            title={t('jira_account_id')}
            errorText={
              errors.jiraAccountId?.message
                ? t(errors.jiraAccountId.message)
                : undefined
            }
            hasError={!!errors.jiraAccountId?.message}
          >
            <Input
              isDisabled={isLoading}
              className="w-full bg-brightGray italic"
              placeHolder={t('insert_id')}
              name="jiraAccountId"
              register={register}
            />
          </FieldInfo>
          <div className="flex flex-row justify-between gap-3">
            <FieldInfo
              title={t('rate_start_date')}
              errorText={
                errors.startDate?.message
                  ? t(errors.startDate.message)
                  : undefined
              }
              hasError={!!errors.startDate?.message}
            >
              <Controller
                name={'startDate'}
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <CustomDatePicker
                    selected={value}
                    onChange={onChange}
                    minDate={minDate}
                    maxDate={maxStartDate}
                  />
                )}
              />
            </FieldInfo>

            <FieldInfo
              title={t('rate_end_date')}
              errorText={
                errors.endDate?.message ? t(errors.endDate.message) : undefined
              }
              hasError={!!errors.endDate?.message}
            >
              <Controller
                name={'endDate'}
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <CustomDatePicker
                    selected={value as Date}
                    onChange={onChange}
                    minDate={minEndDate}
                    disabled={!(type === 'edit' && endDate)}
                  />
                )}
              />
            </FieldInfo>
          </div>
        </div>

        <div className="mt-8 flex justify-between px-2">
          <Button onClick={handleClose} variant="outline" className="h-9 w-24">
            {t('cancel')}
          </Button>
          <Button
            buttonType="submit"
            className="h-9 w-24"
            isDisabled={isLoading}
          >
            {t(type === 'create' ? 'create' : 'confirm')}
            {isLoading && <Spinner className="ml-4 h-5 w-5" />}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
